/**
 * Three-step invite landing flow:
 *
 *   1. Loading — fetch invite metadata, verify cert against bundled SR_pk
 *      and the URL fragment fingerprint (when both are present).
 *   2. OTP gate — phone confirmation step before any upload.
 *   3. Upload checklist — one row per `requested_docs` entry plus an
 *      ad-hoc "מסמך נוסף" row.
 *
 * Errors surface as Hebrew strings via @dovach/i18n. The state machine is
 * intentionally a single useReducer so the render path doesn't duplicate
 * conditional logic across components.
 */

import { useCallback, useEffect, useReducer, useRef, useState } from "react";

import { t } from "@dovach/i18n";
import {
  CertVerifyError,
  fingerprintCertBlob,
  isPlaceholder,
  verifyEnrollmentCert,
} from "@dovach/shared-crypto";

import { ApiError, InviteResponse, RejectedFile, api } from "../lib/api";

type Step =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "otp"; invite: InviteResponse; sending: boolean; sent: boolean; error?: string }
  | {
      kind: "upload";
      invite: InviteResponse;
      uploadToken: string;
      uploads: Record<string, UploadRow>;
      // Files the accountant kicked back. Surfaced as a prominent banner
      // above the checklist so the client knows exactly what to re-upload.
      rejectedFiles: RejectedFile[];
    };

interface UploadFile {
  id: string;            // local-only correlation id
  name: string;          // original filename
  size: number;
  status: "queued" | "uploading" | "done" | "failed" | "cancelled";
  error?: string;
  // Bytes uploaded so far (driven by XHR progress events). Only meaningful
  // when status === "uploading"; on "done" we display 100%.
  loaded?: number;
  // Underlying File ref kept so the user can retry without re-picking. Stored
  // outside React state (in a ref map) because File objects are not safe to
  // structurally serialize / pass through reducer dispatch.
}

interface UploadRow {
  label: string;
  isAdHoc: boolean;      // the user-added "מסמך נוסף" row
  files: UploadFile[];
}

type Action =
  | { type: "load_started" }
  | { type: "loaded"; invite: InviteResponse }
  | { type: "load_failed"; message: string }
  | { type: "otp_sending" }
  | { type: "otp_sent" }
  | { type: "otp_reset" }
  | { type: "otp_verifying" }
  | { type: "otp_failed"; message: string }
  | { type: "otp_verified"; uploadToken: string }
  | { type: "files_queued"; label: string; files: UploadFile[] }
  | { type: "file_status"; label: string; fileId: string; status: UploadFile["status"]; error?: string }
  | { type: "file_progress"; label: string; fileId: string; loaded: number }
  | { type: "ad_hoc_added"; label: string }
  | {
      type: "resume_loaded";
      // Per-label list of synthetic done-rows (one per uploaded file count
      // we learned about from /invite/:id/state). The file list there only
      // gives counts, not filenames, so we render placeholder names.
      synthetic: Record<string, UploadFile[]>;
      rejected: RejectedFile[];
    };

function reducer(state: Step, action: Action): Step {
  switch (action.type) {
    case "load_started":
      return { kind: "loading" };
    case "loaded":
      return {
        kind: "otp",
        invite: action.invite,
        sending: false,
        sent: false,
      };
    case "load_failed":
      return { kind: "error", message: action.message };
    case "otp_sending":
      if (state.kind !== "otp") return state;
      return { ...state, sending: true, error: undefined };
    case "otp_sent":
      if (state.kind !== "otp") return state;
      return { ...state, sending: false, sent: true, error: undefined };
    case "otp_reset":
      if (state.kind !== "otp") return state;
      return { ...state, sending: false, sent: false, error: undefined };
    case "otp_verifying":
      if (state.kind !== "otp") return state;
      return { ...state, sending: true, error: undefined };
    case "otp_failed":
      if (state.kind !== "otp") return state;
      return { ...state, sending: false, error: action.message };
    case "otp_verified":
      if (state.kind !== "otp") return state;
      return {
        kind: "upload",
        invite: state.invite,
        uploadToken: action.uploadToken,
        uploads: Object.fromEntries(
          state.invite.requested_docs.map((d) => [d, { label: d, isAdHoc: false, files: [] }]),
        ),
        rejectedFiles: [],
      };
    case "files_queued": {
      if (state.kind !== "upload") return state;
      const cur = state.uploads[action.label];
      if (!cur) return state;
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.label]: { ...cur, files: [...cur.files, ...action.files] },
        },
      };
    }
    case "file_status": {
      if (state.kind !== "upload") return state;
      const cur = state.uploads[action.label];
      if (!cur) return state;
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.label]: {
            ...cur,
            files: cur.files.map((f) =>
              f.id === action.fileId
                ? { ...f, status: action.status, error: action.error }
                : f,
            ),
          },
        },
      };
    }
    case "file_progress": {
      if (state.kind !== "upload") return state;
      const cur = state.uploads[action.label];
      if (!cur) return state;
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.label]: {
            ...cur,
            files: cur.files.map((f) =>
              f.id === action.fileId ? { ...f, loaded: action.loaded } : f,
            ),
          },
        },
      };
    }
    case "ad_hoc_added": {
      if (state.kind !== "upload") return state;
      if (action.label in state.uploads) return state;
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.label]: { label: action.label, isAdHoc: true, files: [] },
        },
      };
    }
    case "resume_loaded": {
      if (state.kind !== "upload") return state;
      const merged = { ...state.uploads };
      for (const [label, syn] of Object.entries(action.synthetic)) {
        const cur = merged[label];
        if (cur) {
          merged[label] = { ...cur, files: [...syn, ...cur.files] };
        } else {
          merged[label] = { label, isAdHoc: true, files: syn };
        }
      }
      return { ...state, uploads: merged, rejectedFiles: action.rejected };
    }
  }
}

export function InviteFlow({
  inviteId,
  fragmentFp,
}: {
  inviteId: string;
  fragmentFp: string | null;
}) {
  const [state, dispatch] = useReducer(reducer, { kind: "loading" } satisfies Step);
  // Per-(inviteId, fragmentFp) dedup so React Strict Mode's double-mount
  // doesn't double-fetch, BUT a real change to the inputs (e.g. someone
  // edits the URL fragment to a stale value) still re-runs verification.
  // A plain boolean ref pinned the first result forever and turned a
  // stale-fragment hash change into a silent reuse of the prior load.
  const fetchedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const key = `${inviteId}|${fragmentFp ?? ""}`;
    if (fetchedKeyRef.current === key) return;
    fetchedKeyRef.current = key;
    dispatch({ type: "load_started" });
    (async () => {
      try {
        const invite = await api.getInvite(inviteId);

        // Cert verification (per plan/security.md §5.3). If the cert blob is
        // present and the bundled SR_pk is real (not the dev placeholder),
        // we verify both signature and URL-fragment fingerprint pinning.
        if (invite.cert_blob !== null) {
          if (fragmentFp !== null) {
            const computed = fingerprintCertBlob(invite.cert_blob);
            if (computed.toLowerCase() !== fragmentFp.toLowerCase()) {
              dispatch({
                type: "load_failed",
                message: t("client_web.error_cert_fingerprint_mismatch"),
              });
              return;
            }
          }
          if (!isPlaceholder()) {
            try {
              await verifyEnrollmentCert(invite.cert_blob, {
                // Bind the cert's account claim to the API-supplied
                // account_id. Otherwise a swapped cert from a different
                // valid account would pass signature verification.
                expectedAccountId: invite.account_id,
              });
            } catch (err) {
              const reason = err instanceof CertVerifyError ? err.reason : "bad_signature";
              dispatch({
                type: "load_failed",
                message: t("client_web.error_cert_invalid") + " (" + reason + ")",
              });
              return;
            }
          } else {
            // Dev mode with placeholder SR_pk — log loudly and proceed.
            // Production builds fail at build time before placeholder reaches users.

            console.warn(
              "[dovach] SR_pk is placeholder — skipping cert signature verification. " +
                "This is only safe in dev. Generate a real key with `dovach-op init`.",
            );
          }
        }

        dispatch({ type: "loaded", invite });
      } catch (err) {
        const msg =
          err instanceof ApiError ? translateApiError(err.detail) : t("client_web.error_generic");
        dispatch({ type: "load_failed", message: msg });
      }
    })();
  }, [inviteId, fragmentFp]);

  if (state.kind === "loading") {
    return <Center>{t("client_web.loading")}</Center>;
  }
  if (state.kind === "error") {
    return <Center>{state.message}</Center>;
  }
  if (state.kind === "otp") {
    return <OtpGate state={state} dispatch={dispatch} inviteId={inviteId} />;
  }
  return <UploadChecklist state={state} dispatch={dispatch} inviteId={inviteId} />;
}

// ----------------------------------------------------------- OTP gate

function OtpGate({
  state,
  dispatch,
  inviteId,
}: {
  state: Extract<Step, { kind: "otp" }>;
  dispatch: React.Dispatch<Action>;
  inviteId: string;
}) {
  const codeInputRef = useRef<HTMLInputElement>(null);

  // Resend cooldown: backend enforces ~30s per phone, but the UI keeps the
  // user from spamming "Resend" by showing a 60-second countdown. This is
  // also the lockout window we surface after 3 wrong codes.
  const [secondsUntilResend, setSecondsUntilResend] = useState(0);
  // True when the user has burned through enough wrong codes that the
  // backend started returning otp_too_many_attempts. We keep this latched
  // until they get a fresh code (which clears it via otp_reset).
  const [lockedOut, setLockedOut] = useState(false);

  // Tick the resend countdown.
  useEffect(() => {
    if (secondsUntilResend <= 0) return;
    const id = window.setInterval(() => {
      setSecondsUntilResend((s) => Math.max(0, s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [secondsUntilResend]);

  const handleSendCode = async () => {
    if (state.sending) return; // double-submit guard
    if (secondsUntilResend > 0) return;
    dispatch({ type: "otp_sending" });
    try {
      await api.otpSend(inviteId);
      dispatch({ type: "otp_sent" });
      setSecondsUntilResend(60);
      setLockedOut(false);
      // focus the code input after the next render commits.
      setTimeout(() => codeInputRef.current?.focus(), 0);
    } catch (err) {
      const msg =
        err instanceof ApiError ? translateApiError(err.detail) : t("client_web.error_generic");
      dispatch({ type: "otp_failed", message: msg });
    }
  };

  const handleSubmitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state.sending) return; // double-submit guard while a verify is in flight
    const code = codeInputRef.current?.value?.trim() ?? "";
    if (code.length !== 6) {
      dispatch({ type: "otp_failed", message: t("client_web.otp_format") });
      return;
    }
    dispatch({ type: "otp_verifying" });
    try {
      const { upload_token } = await api.otpVerify(inviteId, code);
      dispatch({ type: "otp_verified", uploadToken: upload_token });
    } catch (err) {
      const msg =
        err instanceof ApiError ? translateApiError(err.detail) : t("client_web.error_generic");
      if (err instanceof ApiError && err.detail === "otp_too_many_attempts") {
        setLockedOut(true);
      }
      dispatch({ type: "otp_failed", message: msg });
    }
  };

  const handleResetForResend = () => {
    if (codeInputRef.current) codeInputRef.current.value = "";
    dispatch({ type: "otp_reset" });
    setLockedOut(false);
  };

  return (
    <section className="mt-8 space-y-6">
      <h1 className="text-2xl font-semibold">
        {t("client_web.greeting", { name: state.invite.client_name })}
      </h1>
      <p className="text-slate-600 dark:text-slate-300">
        {t("client_web.campaign_intro", {
          campaign: state.invite.campaign.name,
          period: state.invite.campaign.period_label,
        })}
      </p>

      {lockedOut && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-amber-900">
          {t("client_web.otp_lockout_banner")}
        </p>
      )}

      {!state.sent ? (
        <button
          type="button"
          onClick={handleSendCode}
          disabled={state.sending || secondsUntilResend > 0}
          className="inline-flex h-11 min-w-[8rem] items-center justify-center rounded-lg bg-slate-900 px-4 font-medium text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
        >
          {state.sending ? t("client_web.loading") : t("client_web.otp_send_button")}
        </button>
      ) : (
        <form onSubmit={handleSubmitCode} className="space-y-3">
          <label className="block text-sm font-medium" htmlFor="code">
            {t("client_web.otp_code_label")}
          </label>
          <input
            ref={codeInputRef}
            id="code"
            inputMode="numeric"
            maxLength={6}
            pattern="[0-9]{6}"
            dir="ltr"
            autoFocus
            disabled={state.sending}
            autoComplete="one-time-code"
            className="h-11 w-40 rounded-lg border border-slate-300 px-3 font-mono text-lg tracking-[0.4em] disabled:opacity-50"
          />
          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={state.sending}
              className="inline-flex h-11 min-w-[8rem] items-center justify-center rounded-lg bg-slate-900 px-4 font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {state.sending ? t("client_web.loading") : t("client_web.otp_verify_button")}
            </button>
            <button
              type="button"
              onClick={handleResetForResend}
              disabled={secondsUntilResend > 0}
              className="inline-flex h-11 items-center justify-center rounded-lg border border-slate-300 px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {secondsUntilResend > 0
                ? t("client_web.otp_resend_in_seconds", { seconds: secondsUntilResend })
                : t("client_web.otp_resend")}
            </button>
          </div>
        </form>
      )}

      {state.error && <p className="text-red-600">{state.error}</p>}
    </section>
  );
}

// --------------------------------------------------- upload checklist

// Concurrency cap from plan §3.1: desktop 4, mobile 2. We pick by user-agent.
const CONCURRENCY_CAP = /Mobi|Android|iPhone/i.test(
  typeof navigator !== "undefined" ? navigator.userAgent : "",
)
  ? 2
  : 4;

let __idCounter = 0;
function nextLocalId(): string {
  __idCounter += 1;
  return `f-${Date.now()}-${__idCounter}`;
}

function UploadChecklist({
  state,
  dispatch,
  inviteId,
}: {
  state: Extract<Step, { kind: "upload" }>;
  dispatch: React.Dispatch<Action>;
  inviteId: string;
}) {
  // Track in-flight count across all rows so we honour the global concurrency cap.
  const inFlightRef = useRef(0);
  const queueRef = useRef<{ label: string; fileId: string; file: File }[]>([]);
  // Original File refs by fileId — kept here so retry doesn't need React
  // state (Files aren't pleasant to round-trip through a reducer) and so
  // the cancel button can reach the right AbortController.
  const fileMapRef = useRef(new Map<string, File>());
  const abortMapRef = useRef(new Map<string, AbortController>());

  const drain = useCallback(async () => {
    while (
      inFlightRef.current < CONCURRENCY_CAP &&
      queueRef.current.length > 0
    ) {
      const job = queueRef.current.shift();
      if (!job) break;
      inFlightRef.current += 1;
      const ac = new AbortController();
      abortMapRef.current.set(job.fileId, ac);
      dispatch({
        type: "file_status",
        label: job.label,
        fileId: job.fileId,
        status: "uploading",
      });
      // Fire and forget; each completion calls drain() to start the next.
      void (async () => {
        try {
          await api.uploadWithProgress(
            state.uploadToken,
            job.file,
            job.label,
            (loaded) => {
              dispatch({
                type: "file_progress",
                label: job.label,
                fileId: job.fileId,
                loaded,
              });
            },
            ac.signal,
          );
          dispatch({
            type: "file_status",
            label: job.label,
            fileId: job.fileId,
            status: "done",
          });
        } catch (err) {
          if (ac.signal.aborted || (err as { name?: string })?.name === "AbortError") {
            dispatch({
              type: "file_status",
              label: job.label,
              fileId: job.fileId,
              status: "cancelled",
            });
          } else {
            const detail =
              err instanceof ApiError
                ? translateApiError(err.detail)
                : t("client_web.error_generic");
            dispatch({
              type: "file_status",
              label: job.label,
              fileId: job.fileId,
              status: "failed",
              error: detail,
            });
          }
        } finally {
          abortMapRef.current.delete(job.fileId);
          inFlightRef.current -= 1;
          drain();
        }
      })();
    }
  }, [dispatch, state.uploadToken]);

  // Resume-on-return: ask the server for any previously-uploaded files
  // (counts per document_label) and synthesize a "done" row per count, so
  // the user immediately sees what's already landed instead of treating
  // every reload as a fresh start.
  const resumeFetchedRef = useRef(false);
  useEffect(() => {
    if (resumeFetchedRef.current) return;
    resumeFetchedRef.current = true;
    api
      .getInviteState(inviteId)
      .then((state) => {
        const synthetic: Record<string, UploadFile[]> = {};
        for (const row of state.uploaded) {
          if (row.count <= 0) continue;
          const list: UploadFile[] = [];
          for (let i = 0; i < row.count; i += 1) {
            list.push({
              id: `prior-${row.document_label}-${i}`,
              name: t("client_web.resume_previously_uploaded", {
                count: 1,
                label: row.document_label,
              }),
              size: 0,
              status: "done",
            });
          }
          synthetic[row.document_label] = list;
        }
        const rejected = state.rejected_files ?? [];
        // Fire even with no synthetic rows — a client whose only feedback
        // is a rejection still needs to see the banner.
        if (Object.keys(synthetic).length > 0 || rejected.length > 0) {
          dispatch({ type: "resume_loaded", synthetic, rejected });
        }
      })
      .catch(() => {
        // Silent failure — resume is a UX nicety, not a correctness gate.
      });
  }, [inviteId, dispatch]);

  const cancelFile = (fileId: string) => {
    const ac = abortMapRef.current.get(fileId);
    ac?.abort();
  };

  const retryFile = (label: string, fileId: string) => {
    const file = fileMapRef.current.get(fileId);
    if (!file) return;
    dispatch({
      type: "file_status",
      label,
      fileId,
      status: "queued",
    });
    queueRef.current.push({ label, fileId, file });
    drain();
  };

  const handleAddFiles = (label: string, files: FileList) => {
    if (files.length === 0) return;
    const queued: UploadFile[] = [];
    const jobs: { label: string; fileId: string; file: File }[] = [];
    for (let i = 0; i < files.length; i += 1) {
      const f = files[i];
      const id = nextLocalId();
      queued.push({ id, name: f.name, size: f.size, status: "queued" });
      jobs.push({ label, fileId: id, file: f });
      fileMapRef.current.set(id, f);
    }
    dispatch({ type: "files_queued", label, files: queued });
    queueRef.current.push(...jobs);
    drain();
  };

  const rows = Object.values(state.uploads);
  const flat = rows.flatMap((r) => r.files);
  const totalCount = flat.length;
  const doneCount = flat.filter((f) => f.status === "done").length;

  const [adHocLabel, setAdHocLabel] = useState("");
  const handleAdHoc = (e: React.FormEvent) => {
    e.preventDefault();
    const lbl = adHocLabel.trim();
    if (!lbl) return;
    dispatch({ type: "ad_hoc_added", label: lbl });
    setAdHocLabel("");
  };

  return (
    <section className="mt-8 space-y-4">
      <h2 className="text-xl font-semibold">{t("client_web.checklist_title")}</h2>
      {state.rejectedFiles.length > 0 && (
        <div className="space-y-2 rounded-lg border border-red-300 bg-red-50 p-4">
          <p className="font-semibold text-red-900">
            {t("client_web.rejected_banner_title")}
          </p>
          <ul className="space-y-2">
            {state.rejectedFiles.map((rf) => (
              <li
                key={rf.file_id}
                className="rounded-md bg-white px-3 py-2 text-sm text-red-900"
              >
                <p className="font-medium">
                  {t("client_web.rejected_file_doc", {
                    label: rf.document_label ?? "—",
                  })}
                </p>
                <p>
                  {t("client_web.rejected_file_reason", { reason: rf.reason })}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
      {totalCount > 0 && (
        <p className="text-sm text-slate-600">
          {t("client_web.upload_progress", { current: doneCount, total: totalCount })}
        </p>
      )}
      <ul className="space-y-2">
        {rows.map((row) => (
          <UploadItem
            key={row.label}
            row={row}
            onPick={(files) => handleAddFiles(row.label, files)}
            onCancel={(fileId) => cancelFile(fileId)}
            onRetry={(fileId) => retryFile(row.label, fileId)}
          />
        ))}
      </ul>

      <form onSubmit={handleAdHoc} className="flex gap-2 pt-3">
        <input
          type="text"
          value={adHocLabel}
          onChange={(e) => setAdHocLabel(e.target.value)}
          placeholder={t("client_web.checklist_extra_row")}
          className="h-11 flex-1 rounded-lg border border-slate-300 px-3"
          aria-label={t("client_web.checklist_extra_row")}
        />
        <button
          type="submit"
          disabled={!adHocLabel.trim()}
          className="inline-flex h-11 items-center justify-center rounded-lg border border-slate-300 px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t("client_web.checklist_add_row")}
        </button>
      </form>
    </section>
  );
}

function UploadItem({
  row,
  onPick,
  onCancel,
  onRetry,
}: {
  row: UploadRow;
  onPick: (files: FileList) => void;
  onCancel: (fileId: string) => void;
  onRetry: (fileId: string) => void;
}) {
  const aggregateStatus = (() => {
    if (row.files.length === 0) return "idle" as const;
    if (row.files.some((f) => f.status === "uploading" || f.status === "queued"))
      return "uploading" as const;
    if (row.files.every((f) => f.status === "done")) return "done" as const;
    if (row.files.some((f) => f.status === "failed")) return "failed" as const;
    // Mix of done + cancelled / only cancelled — fall through to uploading
    // for now (the user can retry the cancelled ones).
    return "uploading" as const;
  })();

  return (
    <li className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <span className="font-medium">{row.label}</span>
        <div className="flex items-center gap-3">
          <AggregateStatusPill status={aggregateStatus} count={row.files.length} />
          <label
            className="inline-flex h-11 cursor-pointer items-center justify-center rounded-lg bg-slate-900 px-3 text-sm font-medium text-white"
            aria-label={t("client_web.upload_button") + " - " + row.label}
          >
            {row.files.length === 0
              ? t("client_web.upload_button")
              : t("client_web.upload_add_more")}
            <input
              type="file"
              hidden
              multiple
              onClick={(e) => {
                (e.currentTarget as HTMLInputElement).value = "";
              }}
              onChange={(e) => {
                const fs = e.target.files;
                if (fs && fs.length > 0) onPick(fs);
              }}
            />
          </label>
        </div>
      </div>
      {row.files.length > 0 && (
        <ul className="space-y-1 pr-2 text-sm">
          {row.files.map((f) => (
            <li
              key={f.id}
              className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-1.5"
            >
              <span className="truncate" title={f.name}>
                {f.name}
              </span>
              <div className="flex items-center gap-2">
                <PerFileStatus
                  status={f.status}
                  error={f.error}
                  loaded={f.loaded}
                  size={f.size}
                />
                {(f.status === "uploading" || f.status === "queued") && (
                  <button
                    type="button"
                    onClick={() => onCancel(f.id)}
                    className="text-xs text-slate-600 underline hover:text-slate-900"
                  >
                    {t("client_web.upload_cancel")}
                  </button>
                )}
                {(f.status === "failed" || f.status === "cancelled") && (
                  <button
                    type="button"
                    onClick={() => onRetry(f.id)}
                    className="text-xs text-blue-600 underline hover:text-blue-900"
                  >
                    {t("client_web.upload_retry_button")}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function AggregateStatusPill({
  status,
  count,
}: {
  status: "idle" | "uploading" | "done" | "failed";
  count: number;
}) {
  const labelKey: Record<typeof status, string> = {
    idle: "client_web.upload_state_waiting",
    uploading: "client_web.upload_state_uploading",
    done: "client_web.upload_state_received",
    failed: "client_web.upload_state_failed",
  };
  const colorClasses: Record<typeof status, string> = {
    idle: "bg-slate-100 text-slate-700",
    uploading: "bg-amber-100 text-amber-800",
    done: "bg-emerald-100 text-emerald-800",
    failed: "bg-red-100 text-red-800",
  };
  const text =
    count > 0 ? `${t(labelKey[status])} (${count})` : t(labelKey[status]);
  return (
    <span className={`rounded-full px-3 py-1 text-xs font-medium ${colorClasses[status]}`}>
      {text}
    </span>
  );
}

function PerFileStatus({
  status,
  error,
  loaded,
  size,
}: {
  status: UploadFile["status"];
  error?: string;
  loaded?: number;
  size?: number;
}) {
  if (status === "done")
    return <span className="text-emerald-700">✓ {t("client_web.upload_state_received")}</span>;
  if (status === "failed")
    return (
      <span className="text-red-700" title={error}>
        ✗ {t("client_web.upload_state_failed")}
      </span>
    );
  if (status === "cancelled")
    return <span className="text-slate-500">⨯ {t("client_web.upload_cancelled")}</span>;
  if (status === "uploading") {
    const pct =
      size && size > 0 && loaded !== undefined
        ? Math.min(100, Math.round((loaded / size) * 100))
        : null;
    return (
      <span className="flex items-center gap-2 text-amber-700">
        <span>⟳ {t("client_web.upload_state_uploading")}</span>
        {pct !== null && (
          <span
            className="h-1.5 w-20 rounded-full bg-amber-100"
            aria-label={`upload progress ${pct}%`}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <span
              className="block h-full rounded-full bg-amber-500"
              style={{ width: `${pct}%` }}
            />
          </span>
        )}
        {pct !== null && <span className="text-xs tabular-nums">{pct}%</span>}
      </span>
    );
  }
  return <span className="text-slate-500">{t("client_web.upload_state_waiting")}</span>;
}

function Center({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-12 flex min-h-[40dvh] items-center justify-center text-slate-600">
      {children}
    </div>
  );
}

// ---------------------------------------------- error mapping

function translateApiError(detail: string): string {
  switch (detail) {
    case "invite_not_found":
    case "invite_inconsistent":
      return t("client_web.error_invite_not_found");
    case "client_not_found":
      return t("client_web.error_client_not_found");
    case "invalid_invite_id":
      return t("client_web.error_invalid_invite_id");
    case "invite_expired":
      return t("client_web.error_invite_expired");
    case "client_has_no_phone":
      return t("client_web.error_no_phone");
    case "client_has_no_email":
      return t("client_web.error_no_email");
    case "otp_wrong_code":
      return t("client_web.wrong_otp");
    case "otp_too_many_attempts":
      return t("client_web.error_otp_too_many");
    case "otp_no_code":
    case "otp_expired":
      return t("client_web.otp_expired");
    case "otp_send_too_frequent":
      return t("client_web.error_otp_too_frequent");
    case "campaign_quota_full":
      return t("client_web.campaign_quota_full");
    case "file_too_large":
      return t("client_web.error_file_too_large");
    case "empty_file":
      return t("client_web.error_empty_file");
    case "invalid_upload_token":
    case "upload_token_expired":
      return t("client_web.error_upload_token_expired");
    case "encryption_failed":
      return t("client_web.error_encryption_failed");
    case "http_503":
      return t("client_web.error_http_503");
    case "network_disconnect":
      return t("client_web.error_network_disconnect");
    default:
      // Don't leak the raw API code to end-users in production-style mode.
      // In dev (placeholder SR_pk) we still want the detail for debugging.
      if (isPlaceholder()) {
        return t("client_web.error_generic") + " (" + detail + ")";
      }
      return t("client_web.error_generic");
  }
}
